<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import MmMenu from '../base/MmMenu.vue'
import MmIcon from '../base/MmIcon.vue'
import { studioLinks } from './navigation'
import './studio-tools.css'
const props = defineProps<{ mobile?: boolean }>()
const { t } = useI18n()
const route = useRoute()
const open = ref(false), hydrated = ref(false)
onMounted(() => { hydrated.value = true })
const ids = ['song','lyrics','rap','rapName','cover','video','clips','vocal']
const colors = ['var(--accent)','var(--pop-sun)','var(--pop-tang)','var(--pop-pink)','var(--pop-sky)','var(--pop-violet)','var(--tool-clips)','var(--tool-vocal)']
// Keep the accepted desktop menu's ordered icon + title + description rows.
// The mobile sheet uses the same entries and adds the full tool directory.
// Lyric video sits right after song creation; ids/colors above stay indexed by studioLinks.
const menuLinks = [studioLinks[0], ...studioLinks.filter(link => link.to === '/lyric-video'), ...studioLinks.slice(1).filter(link => link.to !== '/lyric-video')]
const items = computed(() => [
  ...menuLinks.map(link => ({ link, index: studioLinks.indexOf(link) })).map(({ link, index }) => ({
    id: link.to,
    to: link.to,
    label: t(link.to === '/white-noise' ? 'whiteNoise.title' : link.to === '/lyric-video' ? 'lv.title' : props.mobile && index === 0 ? 'mm.shell.song' : `t24a.tools.${ids[index]}.name`),
    icon: link.icon,
    color: link.to === '/white-noise' ? 'var(--pop-sky)' : link.to === '/lyric-video' ? 'var(--accent)' : colors[index],
    meta: t(link.to === '/white-noise' ? 'whiteNoise.description' : link.to === '/lyric-video' ? 'lv.toolDescription' : `t24a.tools.${ids[index]}.desc`),
  })),
  ...(props.mobile ? [
    { divider: true as const },
    { id: '/studio', to: '/studio', label: t('mm.studio.allTools'), icon: 'grid' as const },
  ] : []),
])
watch(() => route.fullPath, () => { open.value = false })
</script>

<template>
  <!-- Native SSR navigation also works without JavaScript; hydration enhances it with MmMenu. -->
  <details v-if="!hydrated" class="studio-ssr-menu">
    <summary :class="mobile ? 'tab-create' : 'studio-btn'">
      <template v-if="mobile"><span class="fab"><MmIcon name="plus" :size="22" /></span><span>{{ t('nav.create') }}</span></template>
      <template v-else>{{ t('mm.shell.studio') }}<MmIcon name="down" :size="16" /></template>
    </summary>
    <nav :aria-label="t('mm.shell.studio')"><template v-for="item in items" :key="item.id"><NuxtLink v-if="item.to" :to="item.to">{{ item.label }}</NuxtLink></template></nav>
  </details>
  <MmMenu v-else v-model="open" :label="t(mobile ? 'mm.shell.makeWhat' : 'mm.shell.studio')" :items="items" :mobile-breakpoint="mobile ? 1024 : 768" :title="mobile ? t('mm.shell.makeWhat') : undefined" variant="mega">
    <template #trigger="{ toggle, expanded, id }">
      <button v-if="mobile" type="button" class="tab-create" :class="{ 'is-current': route.path === '/studio' || route.path.startsWith('/album-cover/') || studioLinks.some(link => route.path === link.to) }" :aria-label="t('nav.create')" aria-haspopup="menu" :aria-expanded="expanded" :aria-controls="expanded ? id : undefined" @click="toggle">
        <span class="fab"><MmIcon name="plus" :size="22" /></span><span>{{ t('nav.create') }}</span>
      </button>
      <button v-else type="button" class="studio-btn" aria-haspopup="menu" :aria-expanded="expanded" :aria-controls="expanded ? id : undefined" @click="toggle">{{ t('mm.shell.studio') }}<MmIcon name="down" :size="16" /></button>
    </template>
  </MmMenu>
</template>

<style scoped>
.studio-ssr-menu { position: relative; }
.studio-ssr-menu summary { list-style: none; cursor: pointer; }
.studio-ssr-menu summary.tab-create { display: grid; justify-items: center; align-content: center; width: 100%; min-height: 52px; }
.studio-ssr-menu summary:focus-visible { outline: 2px solid var(--focus); outline-offset: 2px; }
.studio-ssr-menu summary::-webkit-details-marker { display: none; }
.studio-ssr-menu nav { position: absolute; z-index: var(--z-menu); min-width: 220px; max-height: 70vh; overflow: auto; background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-md); padding: var(--s-2); }
.studio-ssr-menu nav a { display: flex; align-items: center; min-height: 44px; padding: var(--s-2); }
.studio-ssr-menu:has(.tab-create) nav { bottom: 100%; right: 0; }
</style>
